import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// ─── Types ───────────────────────────────────────────────────────────────
// Messages are stored as jsonb. We serialize them as strings across the
// server-function boundary to keep TSR's strict serializable checks happy,
// then JSON.parse on the client back into UIMessage[].

export interface AiThread {
  id: string;
  title: string;
  updated_at: string;
  created_at: string;
}

export interface AiThreadDetail extends AiThread {
  messagesJson: string;
}

// ─── Threads ─────────────────────────────────────────────────────────────

export const listAiThreads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AiThread[]> => {
    const { data, error } = await context.supabase
      .from("ai_threads")
      .select("id, title, updated_at, created_at")
      .eq("agent", "buddha_ka")
      .order("updated_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const createAiThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AiThread> => {
    const { data, error } = await context.supabase
      .from("ai_threads")
      .insert({ user_id: context.userId, agent: "buddha_ka", title: "New conversation" })
      .select("id, title, updated_at, created_at")
      .single();
    if (error) throw new Error(error.message);
    return data;
  });

export const getAiThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ threadId: z.string().uuid() }).parse(raw))
  .handler(async ({ data, context }): Promise<AiThreadDetail | null> => {
    const { data: thread, error: tErr } = await context.supabase
      .from("ai_threads")
      .select("id, title, updated_at, created_at")
      .eq("id", data.threadId)
      .maybeSingle();
    if (tErr) throw new Error(tErr.message);
    if (!thread) return null;

    const { data: rows, error: mErr } = await context.supabase
      .from("ai_messages")
      .select("role, message, created_at")
      .eq("thread_id", data.threadId)
      .order("created_at", { ascending: true });
    if (mErr) throw new Error(mErr.message);

    const messages = (rows ?? [])
      .map((r) => r.message)
      .filter((m) => Boolean(m) && typeof m === "object" && !Array.isArray(m) && Array.isArray((m as { parts?: unknown }).parts));
    return { ...thread, messagesJson: JSON.stringify(messages) };
  });

export const deleteAiThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ threadId: z.string().uuid() }).parse(raw))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("ai_threads").delete().eq("id", data.threadId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const renameAiThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z.object({ threadId: z.string().uuid(), title: z.string().trim().min(1).max(120) }).parse(raw)
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("ai_threads")
      .update({ title: data.title })
      .eq("id", data.threadId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ─── Save a turn (user + assistant) ─────────────────────────────────────
// Messages are passed as JSON strings across the boundary to satisfy the
// serialization checker; parsed back to objects here.

export const saveAiTurn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z.object({
      threadId: z.string().uuid(),
      userMessageJson: z.string().min(2).max(200_000),
      assistantMessageJson: z.string().min(2).max(500_000),
    }).parse(raw)
  )
  .handler(async ({ data, context }) => {
    let userMsg: Record<string, unknown>;
    let assistantMsg: Record<string, unknown>;
    try {
      userMsg = JSON.parse(data.userMessageJson);
      assistantMsg = JSON.parse(data.assistantMessageJson);
    } catch {
      throw new Error("Invalid message JSON");
    }
    const { error: insErr } = await context.supabase.from("ai_messages").insert([
      { thread_id: data.threadId, user_id: context.userId, role: "user", message: userMsg as never },
      { thread_id: data.threadId, user_id: context.userId, role: "assistant", message: assistantMsg as never },
    ]);
    if (insErr) throw new Error(insErr.message);


    // Auto-title from first user message if still default
    const parts = Array.isArray(userMsg.parts) ? (userMsg.parts as Array<{ type?: string; text?: string }>) : [];
    const userText = parts
      .filter((p) => p.type === "text")
      .map((p) => p.text ?? "")
      .join("")
      .trim()
      .slice(0, 80);

    await context.supabase
      .from("ai_threads")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", data.threadId);

    if (userText) {
      // Only rename if it's still the placeholder
      const { data: t } = await context.supabase
        .from("ai_threads")
        .select("title")
        .eq("id", data.threadId)
        .maybeSingle();
      if (t?.title === "New conversation") {
        await context.supabase
          .from("ai_threads")
          .update({ title: userText })
          .eq("id", data.threadId);
      }
    }
    return { ok: true };
  });
